import type { RouteLocation } from 'vue-router';

/**
 * From given flow steps, return the current step based on the current route
 * Which step am I currently on?
 */
export function getCurrentStep(steps: ProductFlowStep[], currentRoute: RouteLocation) {
    const currentRouteName = getRouteName(currentRoute);
    return steps.find(step => step.routeName === currentRouteName);
}

/**
 * From given flow steps, return the index of the current step based on the current route
 * Which step index am I currently on?
 */
export function getCurrentStepIndex(steps: ProductFlowStep[], currentRoute: RouteLocation) {
    const currentRouteName = getRouteName(currentRoute);
    return steps.findIndex(step => step.routeName === currentRouteName);
}

/**
 * In a multi-step flow of routes (e.g. going through multiple steps in a booking process),
 * this function returns the next or previous route in the flow, based on the offset
 *
 * Give me the next or previous step in the flow
 */
export function getAdjacentFlowStep(steps: ProductFlowStep[], currentRoute: RouteLocation, offset: number) {
    const current = getCurrentStep(steps, currentRoute);
    if (!current) {
        return undefined;
    }
    return steps[steps.indexOf(current) + offset];
}

/**
 * Returns the next step in the given flow
 */
export function getNextFlowStep(steps: ProductFlowStep[], currentRoute: RouteLocation) {
    return getAdjacentFlowStep(steps, currentRoute, 1);
}

/**
 * Returns the previous step in the given flow
 */
export function getPreviousFlowStep(steps: ProductFlowStep[], currentRoute: RouteLocation) {
    return getAdjacentFlowStep(steps, currentRoute, -1);
}

/**
 * Returns the localized Route for a given step
 */
export function getStepRoute(step: ProductFlowStep | undefined, productId: ProductId, flowType: FlowType = 'booking', query: Record<string, any> = {}) {
    const localeRoute = useLocaleRoute();

    return step?.routeName
        ? localeRoute({
                name: step.routeName,
                params: {
                    flowType,
                    productKey: getProductKeyFromId(productId),
                    productRouteId: getProductRouteId(productId),
                },
                query,
            })
        : undefined;
}

/**
 * Returns the localized route for the first step in the given flow
 */
export function getFirstFlowRoute(steps: ProductFlowStep[] = [], productId: ProductId, flowType: FlowType = 'booking', query: Record<string, any> = {}) {
    return getStepRoute(steps[0], productId, flowType, query);
}

/**
 * Returns the localized route for the last step in the given flow
 */
export function getLastFlowRoute(steps: ProductFlowStep[] = [], productId: ProductId, flowType: FlowType = 'booking', query: Record<string, any> = {}) {
    return getStepRoute(steps[steps.length - 1], productId, flowType, query);
}

/**
 * TODO
 */
export function getFlowIndexRoute(productId: ProductId, flowType: FlowType = 'booking', query: Record<string, any> = {}) {
    const localeRoute = useLocaleRoute();
    return localeRoute({
        name: 'offerFlow-steps',
        params: {
            flowType,
            productKey: getProductKeyFromId(productId) ?? '',
            productRouteId: getProductRouteId(productId) ?? 1,
        },
        query,
    });
}

/**
 * Returns the route to go to when re-editing a product.
 *
 * Returns the last step, but not the upsell step.
 */
export function getReEditFlowRoute(product: Product) {
    const flowType = product.isUpsell ? 'upsell' : 'booking';
    const query = product.config.cp ? { cp: product.config.cp } : {};
    return getLastFlowRoute(product.config.steps, product.id, flowType, query);
}
